<script setup lang="ts">
import { computed } from 'vue';
import type { COUNTRY_CODE } from '@/utils/constants';
import { useCommissioningDeviceStore } from '../../stores/commissioningDeviceStore';
import { useDeviceSpecificationStore } from '../../stores/deviceSpecificationStore';
import type { Terminal } from '../../models/CommissioningDevice';
import AttentionCircleFilledSVG from '@shared/assets/icon/attention-circle-filled.svg';
import {
  TERMINAL_NO_CONNECTION_DISPLAY_TEXT,
  TERMINAL_CONNECTED_DISPLAY_TEXT,
  PHASE,
} from '../../utils/constants';
import { getPhaseColor } from '../../constants/phase-color';

const props = defineProps<{
  groupNumber: number;
}>();

const commissioningDeviceStore = useCommissioningDeviceStore();
const detectedConnections = computed(() => commissioningDeviceStore.outputConnections);

const deviceSpecificationStore = useDeviceSpecificationStore();
const country = computed(() => deviceSpecificationStore.solshareSite?.country as COUNTRY_CODE);

const terminalsData = computed(() => ({
  L1: {
    label: `L1-${props.groupNumber}`,
    phaseColor: getPhaseColor(country.value, PHASE.L1),
    hasLiveConnection: detectedConnections.value[`L1-${props.groupNumber}` as Terminal]?.ready,
  },
  L2: {
    label: `L2-${props.groupNumber}`,
    phaseColor: getPhaseColor(country.value, PHASE.L2),
    hasLiveConnection: detectedConnections.value[`L2-${props.groupNumber}` as Terminal]?.ready,
  },
  L3: {
    label: `L3-${props.groupNumber}`,
    phaseColor: getPhaseColor(country.value, PHASE.L3),
    hasLiveConnection: detectedConnections.value[`L3-${props.groupNumber}` as Terminal]?.ready,
  },
}));
</script>

<template>
  <div class="review-live-connection-group-container">
    <template v-for="[key, terminal] in Object.entries(terminalsData)" :key="key">
      <a-row>
        <a-col :span="5" class="terminal-label">
          <a-row>
            <a-col
              flex="none"
              class="terminal-label__phase-color-container"
              :style="{ backgroundColor: terminal.phaseColor }"
            ></a-col>
            <a-col flex="auto" class="terminal-label__text-container">
              <div class="terminal-label__text">{{ terminal.label }}</div>
            </a-col>
          </a-row>
        </a-col>

        <a-col
          :span="19"
          :class="[
            'terminal-status',
            terminal.hasLiveConnection ? '' : 'terminal-status__no-connection',
          ]"
        >
          {{
            terminal.hasLiveConnection
              ? TERMINAL_CONNECTED_DISPLAY_TEXT
              : TERMINAL_NO_CONNECTION_DISPLAY_TEXT
          }}
          <AttentionCircleFilledSVG
            v-if="!terminal.hasLiveConnection"
            class="terminal-status__icon"
          />
        </a-col>
      </a-row>
    </template>
  </div>
</template>

<style lang="less" scoped>
.review-live-connection-group-container {
  width: 100%;
  padding-right: @content-side-margin;
}

.terminal-label {
  color: white;
  border-top: 1px solid @color-mid-tone;
  border-bottom: 1px solid @color-mid-tone;

  &__phase-color-container {
    width: @phase-color-width;
  }

  &__text-container {
    background-color: @color-primary;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    min-height: 2.8rem;
    font-weight: @font-weight-medium;
  }
}

.terminal-status {
  display: flex;
  align-items: center;
  padding: 0 0.5rem;
  border-top: 1px solid @color-primary;
  border-bottom: 1px solid @color-primary;
  border-right: 1px solid @color-primary;
  border-top-right-radius: @border-radius-medium;
  border-bottom-right-radius: @border-radius-medium;

  &__no-connection {
    background-color: @color-bg-no-connection;
    font-style: italic;
  }

  &__icon {
    width: 1.5rem;
    height: 1.5rem;
    margin-left: auto;
  }
}
</style>
